import {
  useActiveVkuiLocation,
  useParams,
  useRouteNavigator,
} from '@vkontakte/vk-mini-apps-router';
import { FormItem, Textarea, type NavIdProps } from '@vkontakte/vkui';
import { useState } from 'react';
import { api, errorMessage } from '../../api/client';
import { useLoader } from '../../api/loader';
import { useSession } from '../../api/session';
import type { ModerationVerdict } from '../../api/types';
import { countersChanged } from '../../app/counters';
import { leaveGuarded } from '../../app/leaveGuard';
import { VIEW, VIEW_ROOT_PATH } from '../../app/router';
import { useBack } from '../../app/useBack';
import { t } from '../../i18n';
import { docNumber, versionLabel } from '../../shared/lib/docNumber';
import {
  Action,
  ActionRow,
  CardHeading,
  CardList,
  EmptyState,
  ErrorState,
  ExternalLink,
  FieldLabel,
  Gap,
  Gutter,
  LoadingState,
  Media,
  Meta,
  Muted,
  NoAccess,
  Screen,
  Section,
  SectionTitle,
  StatusBadge,
  Thumb,
  toast,
} from '../../shared/ui';
// Тот же компонент, что и в модерации анкеты: правку питомца модератор
// читает так же — что было и что стало. Картинку он рисует сам, kind='image'.
import { FieldDiff } from '../characters/FieldDiff';
import {
  Notice,
  NoticeBlock,
  SAME_FIELDS_FOLD,
  STALE_MESSAGE,
  canAct,
  canReviewContent,
  findInQueue,
  forgetDecided,
  formatDateTime,
  isConflict,
  personName,
  vkProfileUrl,
} from './staffKit';
import type { PetQueueRow } from './TaskQueuePanel';

/**
 * Разбор версии питомца. Устройство то же, что у предмета склада: публично
 * висит прошлая одобренная версия, а на проверку приходит новая, и отказ её
 * не трогает. Решение уходит вместе с revision — игрок правит заявку, пока
 * она ждёт очереди.
 *
 * Отдельной карточки версии на сервере нет, есть только очередь, поэтому
 * строка ищется постранично по самой очереди.
 */

type Outcome = { verdict: ModerationVerdict; text: string } | null;

const AFTER_LABEL = t('review.claim.after');

/**
 * Между заявкой и одобренной версией могло быть сколько угодно отклонённых:
 * заявка 0005 вполне сравнивается с 0002. Без номера восстановить это по экрану
 * нельзя, а сервер его уже присылает.
 */
function beforeLabelFor(previous: { versionNo: number | null } | null): string {
  const versionNo = previous === null ? null : previous.versionNo;
  return versionNo === null
    ? t('review.claim.before')
    : t('review.claim.beforeVersion', { number: docNumber(versionNo) });
}

/**
 * Поле заявки в сравнении с одобренной версией. Изменённые идут отдельной
 * секцией впереди: ради них экран и открывают, а нетронутые поля подряд
 * заставляли искать правку глазами.
 */
interface ClaimField {
  label: string;
  kind?: 'image';
  before: string | null;
  after: string | null;
  changed: boolean;
}

function ClaimFields({ fields, beforeLabel }: { fields: ClaimField[]; beforeLabel: string }) {
  return (
    <CardList>
      {fields.map((field) => (
        <FieldDiff
          key={field.label}
          label={field.label}
          kind={field.kind}
          before={field.before}
          after={field.after}
          changed={field.changed}
          compact
          thumbKind="pet"
          beforeLabel={beforeLabel}
          afterLabel={AFTER_LABEL}
        />
      ))}
    </CardList>
  );
}

export function PetReviewPanel({ id }: NavIdProps) {
  const session = useSession();
  const back = useBack();
  const routeNavigator = useRouteNavigator();
  const { panel } = useActiveVkuiLocation();
  const params = useParams();

  const active = id !== undefined && panel === id;
  const raw = params?.versionId;
  const versionId = raw && /^\d+$/.test(raw) ? Number(raw) : null;

  const entry = useLoader<PetQueueRow | null>(
    (signal) =>
      findInQueue<PetQueueRow>('/api/pets/queue', (row) => row.versionId === versionId, signal),
    [versionId],
    active && versionId !== null && canReviewContent(session),
  );

  const [busy, setBusy] = useState(false);
  const [stale, setStale] = useState(false);
  const [comment, setComment] = useState('');
  const [outcome, setOutcome] = useState<Outcome>(null);
  const [showSame, setShowSame] = useState(false);

  if (!canReviewContent(session)) {
    return (
      <Screen id={id} title={t('review.pet.title')} back={back}>
        <NoAccess>{t('review.pet.noAccess')}</NoAccess>
      </Screen>
    );
  }

  const row = entry.data;

  async function decide(action: 'approve' | 'reject') {
    if (!row) return;
    // Отказ без причины сервер не примет, и игроку он бесполезен.
    const reason = comment.trim();
    if (action === 'reject' && reason === '') return;
    setBusy(true);
    try {
      const body =
        action === 'approve'
          ? { revision: row.revision }
          : { revision: row.revision, comment: reason };
      await api.post(`/api/pets/versions/${row.versionId}/${action}`, body);

      forgetDecided('pet', row.versionId);
      countersChanged();
      setStale(false);
      // Результат решения — тост, а не блок на экране: это ответ на действие,
      // а не состояние заявки. Кнопка в нём ведёт в очередь адресом, а не
      // `back()`: тост переживает экран, и вернувшегося модератора шаг назад
      // увёл бы из очереди.
      const result: NonNullable<Outcome> =
        action === 'approve'
          ? { verdict: 'approve', text: t('review.pet.approved') }
          : { verdict: 'reject', text: t('review.pet.rejected') };
      setOutcome(result);
      toast(result.text, {
        label: t('common.action.backToQueue'),
        onClick: () => leaveGuarded(() => routeNavigator.replace(VIEW_ROOT_PATH[VIEW.TASKS])),
      });
    } catch (cause: unknown) {
      // Заявку можно править, пока она ждёт очереди, поэтому молча повторять
      // запрос нельзя: решение относилось бы уже к другому тексту.
      if (isConflict(cause)) {
        setStale(true);
        entry.reload();
      } else {
        toast(errorMessage(cause));
      }
    } finally {
      setBusy(false);
    }
  }

  const writable = canAct(session);
  const previous = row?.published ?? null;
  // Снимок заявки — в шапку, как у разбора анкеты. Кадр стоит и без файла:
  // в нём сота, и шапка не меняет вид от того, приложили фото или нет.
  const image = row?.imageId ? { id: row.imageId } : null;
  const beforeLabel = beforeLabelFor(previous);

  const fields: ClaimField[] =
    row === null
      ? []
      : [
          {
            label: t('common.field.nickname'),
            before: previous?.name ?? null,
            after: row.name,
            changed: previous !== null && previous.name !== row.name,
          },
          {
            label: t('common.field.kind'),
            before: previous?.species ?? null,
            after: row.species,
            changed: previous !== null && previous.species !== row.species,
          },
          {
            label: t('common.field.description'),
            before: previous?.description ?? null,
            after: row.description,
            changed: previous !== null && previous.description !== row.description,
          },
          {
            label: t('review.pet.field.photo'),
            kind: 'image',
            before: previous?.imageId ?? null,
            after: row.imageId,
            changed: previous !== null && previous.imageId !== row.imageId,
          },
          {
            label: t('review.pet.field.passport'),
            kind: 'image',
            before: previous?.passportImageId ?? null,
            after: row.passportImageId,
            changed: previous !== null && previous.passportImageId !== row.passportImageId,
          },
        ];

  const changedFields = fields.filter((field) => field.changed);
  const sameFields = fields.filter((field) => !field.changed);
  // Заявку без одобренной версии сравнивать не с чем: там «нетронуто» всё,
  // и прятать её под кнопку значило бы спрятать саму заявку.
  // Прячем за кнопку только длинный хвост: три строки за раскрывашкой —
  // это лишнее нажатие ради экономии трёх строк.
  const foldSame = sameFields.length > SAME_FIELDS_FOLD;
  const sameOpen = changedFields.length === 0 || !foldSame || showSame;

  /*
    Состояние заявки — в шапке экрана: разбор открывают, уже зная, что
    проверяют, и первым делом смотрят, решена ли она.
  */
  return (
    <Screen
      id={id}
      title={t('review.pet.title')}
      back={back}
      after={
        row ? <StatusBadge tone="pending">{t('review.claim.pending')}</StatusBadge> : undefined
      }
    >
      {entry.loading && !row ? <LoadingState text={t('review.claim.loading')} /> : null}
      {entry.error ? <ErrorState message={entry.error} onRetry={entry.reload} /> : null}

      {!entry.loading && !entry.error && !row && !outcome ? (
        <EmptyState title={t('review.claim.notFound')}>{t('review.pet.notFound')}</EmptyState>
      ) : null}

      {row ? (
        <>
          <Section>
            <Gutter>
              <Media aside={<Thumb image={image} kind="pet" alt={row.name} gallery={[image]} />}>
                <CardHeading>{row.name}</CardHeading>

                <Gap step="s2" />
                <Meta>
                  {[
                    row.characterName?.trim() || t('review.claim.noName'),
                    // У первой версии номер ничего не различает: очередь его
                    // прячет по тому же правилу, и карточка не должна расходиться
                    // со строкой, из которой в неё перешли.
                    row.versionNo > 1 ? versionLabel(row.versionNo) : null,
                    formatDateTime(row.submittedAt),
                  ]}
                </Meta>

                <Gap step="s1h" />
                <Muted>
                  {t('review.claim.sentBy')}{' '}
                  <ExternalLink href={vkProfileUrl(row.author.vkUserId)}>
                    {personName(row.author)}
                  </ExternalLink>
                </Muted>
              </Media>

              {row.isInitial ? (
                <>
                  <Gap step="s3" />
                  <Muted>{t('review.pet.initial')}</Muted>
                </>
              ) : null}
            </Gutter>
          </Section>

          {stale ? (
            <NoticeBlock
              tone="warning"
              title={STALE_MESSAGE}
              action={
                <Action tone="quiet" size="s" onClick={entry.reload}>
                  {t('common.action.refresh')}
                </Action>
              }
            >
              {t('review.pet.stale')}
            </NoticeBlock>
          ) : null}
        </>
      ) : null}

      {row ? (
        <>
          {changedFields.length > 0 ? (
            <Section>
              <SectionTitle accent="pink">
                {t('review.claim.changed', { count: changedFields.length })}
              </SectionTitle>
              <ClaimFields fields={changedFields} beforeLabel={beforeLabel} />
            </Section>
          ) : null}

          {sameFields.length > 0 ? (
            <Section>
              <SectionTitle>
                {changedFields.length > 0 ? t('review.claim.same') : t('review.claim.proposal')}
              </SectionTitle>

              {changedFields.length > 0 && foldSame ? (
                <Gutter>
                  <Action
                    tone="quiet"
                    size="m"
                    stretched
                    aria-expanded={showSame}
                    onClick={() => setShowSame(!showSame)}
                  >
                    {showSame
                      ? t('common.action.collapse')
                      : t('review.claim.showMore', { count: sameFields.length })}
                  </Action>
                </Gutter>
              ) : null}

              {sameOpen ? <ClaimFields fields={sameFields} beforeLabel={beforeLabel} /> : null}
            </Section>
          ) : null}
        </>
      ) : null}

      {row && !outcome ? (
        <Section>
          <SectionTitle accent="pink">{t('review.decision.title')}</SectionTitle>
          {/* Комментарий полем, а не диалогом: причина отказа пишется рядом с
              текстом заявки, который её и объясняет. */}
          <FormItem top={<FieldLabel>{t('review.decision.comment.label')}</FieldLabel>}>
            <Textarea
              value={comment}
              rows={3}
              disabled={busy || !writable}
              placeholder={t('review.decision.comment.placeholder')}
              onChange={(event) => setComment(event.target.value)}
            />
          </FormItem>

          {!writable ? (
            <Gutter>
              <Notice tone="warning" title={t('review.decision.readOnly.title')}>
                {t('review.decision.readOnly.text')}
              </Notice>
            </Gutter>
          ) : null}

          <Gutter>
            <ActionRow>
              <Action
                tone="action"
                size="l"
                stretched
                loading={busy}
                disabled={busy || !writable}
                onClick={() => void decide('approve')}
              >
                {t('review.decision.approve')}
              </Action>
              <Action
                tone="quiet"
                size="l"
                stretched
                disabled={busy || !writable || comment.trim() === ''}
                onClick={() => void decide('reject')}
              >
                {t('review.decision.reject')}
              </Action>
            </ActionRow>
          </Gutter>
        </Section>
      ) : null}
    </Screen>
  );
}
